import { useState } from 'react';
import {
  useDeleteReportFavoriteMutation,
  useDeleteReportMutation,
  useFoldersShortQuery,
  useMakeReportFavoriteMutation,
  useOwnProfileQuery,
  useReportFolderQuery,
  useReportsQuery,
} from '@common/cache';
import { toast } from 'react-toastify';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { ReportingTableEntry } from '@views/pages/Reporting/Reports/hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { useReportStore } from '@store';
import { ReportsFolderPreviewFilters } from '@views/pages/Reporting/ReportsFolderPreview/types.ts';
import { isEqual } from 'lodash';

const initialFilters: ReportsFolderPreviewFilters = {
  search: '',
  page: 1,
  limit: 100,
  sort_by: 'name',
  sort_order: 'ASC',
  report_type: undefined,
};

export const useReportsFolderPreview = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { data: profile = { id: '' } } = useOwnProfileQuery();
  const { data: foldersList = [] } = useFoldersShortQuery();
  const { data: folderDetails } = useReportFolderQuery(params.folder_id || '');

  const [filters, setFilters] =
    useState<ReportsFolderPreviewFilters>(initialFilters);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [reportIdToDelete, setReportIdToDelete] = useState('');
  const [reportIdToFavorite, setReportIdToFavorite] = useState('');
  const [isEditReportFolderModalOpen, setIsEditReportFolderModalOpen] =
    useState(false);

  const isInitialFilters = isEqual(filters, initialFilters);

  const { clearAll, setReport } = useReportStore();
  const folder = foldersList.find(item => item.id === params.folder_id);

  const {
    data = {
      reports: [],
      total: 0,
    },
    isPending,
  } = useReportsQuery({
    ...filters,
    report_folder_id: params.folder_id,
  });

  const {
    mutateAsync: deleteReportMutation,
    isPending: deleteReportIsLoading,
  } = useDeleteReportMutation();

  const {
    mutateAsync: makeReportFavoriteMutation,
    isPending: makeReportFavoriteIsPending,
  } = useMakeReportFavoriteMutation();

  const {
    mutateAsync: deleteReportFavoriteMutation,
    isPending: deleteReportFavoriteIsPending,
  } = useDeleteReportFavoriteMutation();

  const handleBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate('/reporting/folders');
    }
  };

  const handleCloseEditReportFolderModal = () => {
    setIsEditReportFolderModalOpen(false);
  };

  const handleOpenEditReportFolderModal = () => {
    setIsEditReportFolderModalOpen(true);
  };

  const handleFilterChange = (
    newFilters: Partial<ReportsFolderPreviewFilters>,
  ) => {
    setFilters({ ...filters, page: 1, ...newFilters });
  };

  const handleOpenDeleteModal = (id: string) => {
    setIsDeleteModalOpen(true);
    setReportIdToDelete(id);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setReportIdToDelete('');
  };

  const handleDeleteReport = async () => {
    try {
      await deleteReportMutation(reportIdToDelete);

      toast.success('Report deleted successfully.');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      handleCloseDeleteModal();
    }
  };

  const handleMakeReportFavorite = async (id: string, isFavorite: boolean) => {
    setReportIdToFavorite(id);
    const action = isFavorite
      ? deleteReportFavoriteMutation
      : makeReportFavoriteMutation;

    try {
      await action(id);

      toast.success(
        isFavorite
          ? 'Report deleted from favorite successfully'
          : 'Report set as favorite successfully.',
      );
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setReportIdToFavorite('');
    }
  };

  const entries: ReportingTableEntry[] = data.reports.map(item => {
    return {
      ...item,
      created_by: {
        id: item.created_by?.id || '-',
        name: item.created_by?.name || '-',
      },
      folder: {
        id: item.folder.id,
        name: item.folder.name,
        organization: {
          id: item?.folder?.organization?.id || '',
          name: item?.folder?.organization?.name || '',
        },
      },
    };
  });

  const handleAddReport = () => {
    clearAll();
    if (folder) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setReport({ report_folder: folder, report_folder_id: folder.id } as any);
    }
    navigate('/reporting/create');
  };

  return {
    filters,
    entries,
    handleFilterChange,
    totalCount: data.total,
    loading: isPending,
    isDeleteModalOpen,
    handleOpenDeleteModal,
    handleCloseDeleteModal,
    handleDeleteReport,
    deleteReportIsLoading,
    makeReportFavoriteIsPending:
      makeReportFavoriteIsPending || deleteReportFavoriteIsPending,
    handleMakeReportFavorite,
    reportIdToFavorite,
    handleBack,
    handleAddReport,
    folderTitle: folder?.name || '',
    isEditReportFolderModalOpen,
    handleCloseEditReportFolderModal,
    handleOpenEditReportFolderModal,
    folder_id: params.folder_id || '',
    editIsDisabled: folderDetails?.created_by?.id !== profile.id,
    isInitialFilters,
  };
};
